import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface TeacherDailyReportProps {
  domain: string;
}

interface DailyReport {
  id: string;
  teacherId: string;
  teacherName: string;
  className: string;
  section: string;
  subject: string;
  topicsCovered: string;
  curriculumTopics: string[];
  periodsSpent: number;
  remarks: string;
  date: string;
  dateMillis: number;
  inputMode: string;
  createdAt: number;
  updatedAt: number;
  tenantId: string;
}

interface TeacherSummary {
  teacherId: string;
  teacherName: string;
  totalReports: number;
  totalPeriods: number;
  subjects: Set<string>;
  classes: Set<string>;
}

const TeacherDailyReport: React.FC<TeacherDailyReportProps> = ({ domain }) => {
  const [reports, setReports] = useState<DailyReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [selectedTeacher, setSelectedTeacher] = useState<string>('all');
  const [selectedSubject, setSelectedSubject] = useState<string>('all');
  const [selectedClass, setSelectedClass] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'timeline' | 'teacher' | 'table'>('timeline');
  const [expandedTeacher, setExpandedTeacher] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<'today' | 'week' | 'month' | 'custom'>('today');
  const [customStartDate, setCustomStartDate] = useState<string>(selectedDate);
  const [customEndDate, setCustomEndDate] = useState<string>(selectedDate);

  // Compute effective date range
  const getDateRange = (): { start: string; end: string } => {
    const today = new Date();
    switch (dateRange) {
      case 'today':
        return { start: selectedDate, end: selectedDate };
      case 'week': {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay() + 1); // Monday
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 5); // Saturday
        return {
          start: weekStart.toISOString().split('T')[0],
          end: weekEnd.toISOString().split('T')[0],
        };
      }
      case 'month': {
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return {
          start: monthStart.toISOString().split('T')[0],
          end: monthEnd.toISOString().split('T')[0],
        };
      }
      case 'custom':
        return { start: customStartDate, end: customEndDate };
      default:
        return { start: selectedDate, end: selectedDate };
    }
  };

  useEffect(() => {
    fetchReports();
  }, [domain, selectedDate, dateRange, customStartDate, customEndDate]);

  const fetchReports = async () => {
    if (!domain) return;
    setLoading(true);

    try {
      const reportsRef = collection(db, `school-data/${domain}/daily-reports`);
      const { start, end } = getDateRange();

      const q = query(
        reportsRef,
        where('date', '>=', start),
        where('date', '<=', end),
        orderBy('date', 'desc')
      );

      const snapshot = await getDocs(q);
      const fetched: DailyReport[] = [];
      snapshot.forEach((doc) => {
        fetched.push({ id: doc.id, ...doc.data() } as DailyReport);
      });

      // Sort by createdAt descending within each date
      fetched.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setReports(fetched);
    } catch (error) {
      console.error('Error fetching daily reports:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter reports
  const filteredReports = useMemo(() => {
    return reports.filter(r => {
      if (selectedTeacher !== 'all' && r.teacherId !== selectedTeacher) return false;
      if (selectedSubject !== 'all' && r.subject !== selectedSubject) return false;
      if (selectedClass !== 'all' && r.className !== selectedClass) return false;
      return true;
    });
  }, [reports, selectedTeacher, selectedSubject, selectedClass]);

  // Unique filter values
  const teachers = useMemo(() => {
    const map = new Map<string, string>();
    reports.forEach(r => map.set(r.teacherId, r.teacherName));
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [reports]);

  const subjects = useMemo(() => {
    return [...new Set(reports.map(r => r.subject))].filter(Boolean).sort();
  }, [reports]);

  const classes = useMemo(() => {
    return [...new Set(reports.map(r => r.className))].filter(Boolean).sort((a, b) => Number(a) - Number(b));
  }, [reports]);

  // Teacher summaries for teacher view
  const teacherSummaries = useMemo(() => {
    const map = new Map<string, TeacherSummary>();
    filteredReports.forEach(r => {
      if (!map.has(r.teacherId)) {
        map.set(r.teacherId, {
          teacherId: r.teacherId,
          teacherName: r.teacherName,
          totalReports: 0,
          totalPeriods: 0,
          subjects: new Set(),
          classes: new Set(),
        });
      }
      const s = map.get(r.teacherId)!;
      s.totalReports++;
      s.totalPeriods += r.periodsSpent || 0;
      if (r.subject) s.subjects.add(r.subject);
      if (r.className) s.classes.add(`${r.className}-${r.section}`);
    });
    return Array.from(map.values()).sort((a, b) => b.totalPeriods - a.totalPeriods);
  }, [filteredReports]);

  // Group reports by date for timeline view
  const reportsByDate = useMemo(() => {
    const map = new Map<string, DailyReport[]>();
    filteredReports.forEach(r => {
      if (!map.has(r.date)) map.set(r.date, []);
      map.get(r.date)!.push(r);
    });
    return Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [filteredReports]);

  // Stats
  const stats = useMemo(() => {
    const uniqueTeachers = new Set(filteredReports.map(r => r.teacherId));
    const totalPeriods = filteredReports.reduce((sum, r) => sum + (r.periodsSpent || 0), 0);
    const uniqueSubjects = new Set(filteredReports.map(r => r.subject));
    const uniqueClasses = new Set(filteredReports.map(r => `${r.className}-${r.section}`));
    return {
      totalReports: filteredReports.length,
      uniqueTeachers: uniqueTeachers.size,
      totalPeriods,
      uniqueSubjects: uniqueSubjects.size,
      uniqueClasses: uniqueClasses.size,
    };
  }, [filteredReports]);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatTime = (millis: number) => {
    if (!millis) return '-';
    const d = new Date(millis);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const getSubjectColor = (subject: string): string => {
    const colors: Record<string, string> = {
      'Mathematics': '#E53935',
      'Science': '#43A047',
      'English': '#1E88E5',
      'Hindi': '#FB8C00',
      'Social Science': '#8E24AA',
      'Physics': '#00ACC1',
      'Chemistry': '#D81B60',
      'Biology': '#6D4C41',
      'Computer Science': '#546E7A',
      'Physical Education': '#F4511E',
    };
    return colors[subject] || '#5C2597';
  };

  return (
    <div>
      {/* Stats Cards */}
      <div className="row g-3 mb-4">
        {[
          { label: 'Total Reports', value: stats.totalReports, icon: 'bi-file-earmark-text', color: '#5C2597' },
          { label: 'Teachers Reported', value: stats.uniqueTeachers, icon: 'bi-person-check', color: '#2D7DD2' },
          { label: 'Periods Covered', value: stats.totalPeriods, icon: 'bi-clock-history', color: '#28A745' },
          { label: 'Subjects Taught', value: stats.uniqueSubjects, icon: 'bi-book', color: '#FFC107' },
          { label: 'Classes Covered', value: stats.uniqueClasses, icon: 'bi-people', color: '#E83E8C' },
        ].map((stat, idx) => (
          <div key={idx} className="col-6 col-md-4 col-xl">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body d-flex align-items-center p-3">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                  style={{ width: '44px', height: '44px', backgroundColor: `${stat.color}15`, color: stat.color }}
                >
                  <i className={`bi ${stat.icon} fs-5`}></i>
                </div>
                <div>
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>{stat.label}</div>
                  <div className="fw-bold fs-5">{stat.value}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            {/* Date Range Selector */}
            <div className="col-12 col-md-auto">
              <label className="form-label small fw-semibold text-muted mb-1">Date Range</label>
              <div className="btn-group w-100" role="group">
                {(['today', 'week', 'month', 'custom'] as const).map(range => (
                  <button
                    key={range}
                    className={`btn btn-sm ${dateRange === range ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setDateRange(range)}
                    style={dateRange === range ? { backgroundColor: '#5C2597', borderColor: '#5C2597' } : {}}
                  >
                    {range === 'today' ? 'Day' : range.charAt(0).toUpperCase() + range.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Date Picker(s) */}
            {dateRange === 'today' && (
              <div className="col-12 col-md-2">
                <label className="form-label small fw-semibold text-muted mb-1">Date</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              </div>
            )}
            {dateRange === 'custom' && (
              <>
                <div className="col-6 col-md-2">
                  <label className="form-label small fw-semibold text-muted mb-1">From</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                  />
                </div>
                <div className="col-6 col-md-2">
                  <label className="form-label small fw-semibold text-muted mb-1">To</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                  />
                </div>
              </>
            )}

            {/* Teacher Filter */}
            <div className="col-6 col-md-2">
              <label className="form-label small fw-semibold text-muted mb-1">Teacher</label>
              <select className="form-select form-select-sm" value={selectedTeacher} onChange={(e) => setSelectedTeacher(e.target.value)}>
                <option value="all">All Teachers</option>
                {teachers.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            </div>

            {/* Subject Filter */}
            <div className="col-6 col-md-2">
              <label className="form-label small fw-semibold text-muted mb-1">Subject</label>
              <select className="form-select form-select-sm" value={selectedSubject} onChange={(e) => setSelectedSubject(e.target.value)}>
                <option value="all">All Subjects</option>
                {subjects.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Class Filter */}
            <div className="col-6 col-md-2">
              <label className="form-label small fw-semibold text-muted mb-1">Class</label>
              <select className="form-select form-select-sm" value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}>
                <option value="all">All Classes</option>
                {classes.map(c => (
                  <option key={c} value={c}>Class {c}</option>
                ))}
              </select>
            </div>

            {/* View Mode */}
            <div className="col-6 col-md-auto ms-md-auto">
              <label className="form-label small fw-semibold text-muted mb-1">View</label>
              <div className="btn-group w-100" role="group">
                <button
                  className={`btn btn-sm ${viewMode === 'timeline' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setViewMode('timeline')}
                  style={viewMode === 'timeline' ? { backgroundColor: '#5C2597', borderColor: '#5C2597' } : {}}
                  title="Timeline View"
                >
                  <i className="bi bi-layout-text-sidebar-reverse"></i>
                </button>
                <button
                  className={`btn btn-sm ${viewMode === 'teacher' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setViewMode('teacher')}
                  style={viewMode === 'teacher' ? { backgroundColor: '#5C2597', borderColor: '#5C2597' } : {}}
                  title="Teacher View"
                >
                  <i className="bi bi-person-lines-fill"></i>
                </button>
                <button
                  className={`btn btn-sm ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setViewMode('table')}
                  style={viewMode === 'table' ? { backgroundColor: '#5C2597', borderColor: '#5C2597' } : {}}
                  title="Table View"
                >
                  <i className="bi bi-table"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="text-muted mt-2">Loading daily reports...</p>
        </div>
      )}

      {/* No Data */}
      {!loading && filteredReports.length === 0 && (
        <div className="card border-0 shadow-sm">
          <div className="card-body text-center py-5">
            <i className="bi bi-journal-x fs-1 text-muted mb-3 d-block"></i>
            <h5 className="text-muted">No Reports Found</h5>
            <p className="text-muted small">No daily reports were submitted for the selected filters.</p>
          </div>
        </div>
      )}

      {/* Timeline View */}
      {!loading && filteredReports.length > 0 && viewMode === 'timeline' && (
        <div>
          {reportsByDate.map(([date, dayReports]) => (
            <div key={date} className="mb-4">
              <div className="d-flex align-items-center mb-3">
                <div
                  className="rounded-pill px-3 py-1 text-white fw-semibold small"
                  style={{ backgroundColor: '#5C2597' }}
                >
                  <i className="bi bi-calendar3 me-2"></i>
                  {formatDate(date)}
                </div>
                <span className="badge bg-secondary ms-2">{dayReports.length} report{dayReports.length !== 1 ? 's' : ''}</span>
              </div>

              <div className="row g-3">
                {dayReports.map((report) => (
                  <div key={report.id} className="col-12 col-md-6 col-xl-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div
                        className="card-header border-0 text-white d-flex align-items-center justify-content-between py-2 px-3"
                        style={{ backgroundColor: getSubjectColor(report.subject) }}
                      >
                        <span className="fw-semibold small">
                          <i className="bi bi-book me-1"></i>{report.subject}
                        </span>
                        <span className="badge bg-white text-dark" style={{ fontSize: '0.7rem' }}>
                          Class {report.className}-{report.section}
                        </span>
                      </div>
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center mb-2">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0"
                            style={{ width: '32px', height: '32px', backgroundColor: '#5C259715', color: '#5C2597', fontSize: '0.75rem', fontWeight: 'bold' }}
                          >
                            {report.teacherName?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="fw-semibold small">{report.teacherName}</div>
                            <div className="text-muted" style={{ fontSize: '0.7rem' }}>{report.teacherId}</div>
                          </div>
                        </div>

                        <div className="mb-2">
                          <div className="text-muted small mb-1">
                            <i className="bi bi-chat-square-text me-1"></i>Topics Covered
                          </div>
                          <div className="small fw-medium">{report.topicsCovered || '-'}</div>
                        </div>

                        {report.curriculumTopics && report.curriculumTopics.length > 0 && (
                          <div className="mb-2">
                            <div className="text-muted small mb-1">
                              <i className="bi bi-list-check me-1"></i>Curriculum Topics
                            </div>
                            <div className="d-flex flex-wrap gap-1">
                              {report.curriculumTopics.map((topic, i) => (
                                <span key={i} className="badge bg-light text-dark border" style={{ fontSize: '0.65rem' }}>
                                  {topic}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {report.remarks && (
                          <div className="mb-2">
                            <div className="text-muted small mb-1">
                              <i className="bi bi-pencil me-1"></i>Remarks
                            </div>
                            <div className="small fst-italic text-secondary">{report.remarks}</div>
                          </div>
                        )}

                        <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                          <span className="badge bg-light text-dark border">
                            <i className="bi bi-clock me-1"></i>{report.periodsSpent} period{report.periodsSpent !== 1 ? 's' : ''}
                          </span>
                          <span className="text-muted" style={{ fontSize: '0.65rem' }}>
                            <i className="bi bi-upload me-1"></i>
                            {report.inputMode === 'manual' ? 'Manual' : report.inputMode}
                            {' '}at {formatTime(report.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Teacher Summary View */}
      {!loading && filteredReports.length > 0 && viewMode === 'teacher' && (
        <div className="row g-3">
          {teacherSummaries.map((teacher) => {
            const isExpanded = expandedTeacher === teacher.teacherId;
            const teacherReports = filteredReports.filter(r => r.teacherId === teacher.teacherId);

            return (
              <div key={teacher.teacherId} className="col-12">
                <div className="card border-0 shadow-sm">
                  <div
                    className="card-header bg-white border-0 py-3 px-4"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setExpandedTeacher(isExpanded ? null : teacher.teacherId)}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                          style={{ width: '44px', height: '44px', backgroundColor: '#5C259715', color: '#5C2597', fontWeight: 'bold' }}
                        >
                          {teacher.teacherName?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="fw-bold">{teacher.teacherName}</div>
                          <div className="text-muted small">{teacher.teacherId}</div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-3">
                        <div className="text-center">
                          <div className="fw-bold" style={{ color: '#5C2597' }}>{teacher.totalReports}</div>
                          <div className="text-muted" style={{ fontSize: '0.65rem' }}>Reports</div>
                        </div>
                        <div className="text-center">
                          <div className="fw-bold" style={{ color: '#28A745' }}>{teacher.totalPeriods}</div>
                          <div className="text-muted" style={{ fontSize: '0.65rem' }}>Periods</div>
                        </div>
                        <div className="text-center">
                          <div className="fw-bold" style={{ color: '#2D7DD2' }}>{teacher.subjects.size}</div>
                          <div className="text-muted" style={{ fontSize: '0.65rem' }}>Subjects</div>
                        </div>
                        <div className="text-center">
                          <div className="fw-bold" style={{ color: '#E83E8C' }}>{teacher.classes.size}</div>
                          <div className="text-muted" style={{ fontSize: '0.65rem' }}>Classes</div>
                        </div>
                        <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'} ms-2`}></i>
                      </div>
                    </div>

                    {/* Subject badges */}
                    <div className="d-flex flex-wrap gap-1 mt-2">
                      {[...teacher.subjects].map(s => (
                        <span
                          key={s}
                          className="badge text-white"
                          style={{ backgroundColor: getSubjectColor(s), fontSize: '0.65rem' }}
                        >
                          {s}
                        </span>
                      ))}
                      {[...teacher.classes].map(c => (
                        <span key={c} className="badge bg-light text-dark border" style={{ fontSize: '0.65rem' }}>
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="card-body pt-0 px-4">
                      <div className="table-responsive">
                        <table className="table table-sm table-hover mb-0">
                          <thead>
                            <tr className="text-muted small">
                              <th>Date</th>
                              <th>Subject</th>
                              <th>Class</th>
                              <th>Topics Covered</th>
                              <th>Periods</th>
                              <th>Remarks</th>
                              <th>Submitted</th>
                            </tr>
                          </thead>
                          <tbody>
                            {teacherReports.map(r => (
                              <tr key={r.id}>
                                <td className="small">{formatDate(r.date)}</td>
                                <td>
                                  <span
                                    className="badge text-white"
                                    style={{ backgroundColor: getSubjectColor(r.subject), fontSize: '0.7rem' }}
                                  >
                                    {r.subject}
                                  </span>
                                </td>
                                <td className="small">{r.className}-{r.section}</td>
                                <td className="small" style={{ maxWidth: '250px' }}>{r.topicsCovered || '-'}</td>
                                <td className="small text-center">{r.periodsSpent}</td>
                                <td className="small text-muted" style={{ maxWidth: '150px' }}>
                                  {r.remarks || '-'}
                                </td>
                                <td className="small text-muted">{formatTime(r.createdAt)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Table View */}
      {!loading && filteredReports.length > 0 && viewMode === 'table' && (
        <div className="card border-0 shadow-sm">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr className="small">
                    <th className="ps-3">Date</th>
                    <th>Teacher</th>
                    <th>Subject</th>
                    <th>Class</th>
                    <th>Topics Covered</th>
                    <th>Curriculum Topics</th>
                    <th className="text-center">Periods</th>
                    <th>Remarks</th>
                    <th>Submitted At</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReports.map(r => (
                    <tr key={r.id}>
                      <td className="ps-3 small text-nowrap">{formatDate(r.date)}</td>
                      <td>
                        <div className="small fw-semibold">{r.teacherName}</div>
                        <div className="text-muted" style={{ fontSize: '0.65rem' }}>{r.teacherId}</div>
                      </td>
                      <td>
                        <span
                          className="badge text-white"
                          style={{ backgroundColor: getSubjectColor(r.subject), fontSize: '0.7rem' }}
                        >
                          {r.subject}
                        </span>
                      </td>
                      <td className="small">{r.className}-{r.section}</td>
                      <td className="small" style={{ maxWidth: '220px' }}>{r.topicsCovered || '-'}</td>
                      <td style={{ maxWidth: '180px' }}>
                        {r.curriculumTopics && r.curriculumTopics.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {r.curriculumTopics.map((t, i) => (
                              <span key={i} className="badge bg-light text-dark border" style={{ fontSize: '0.6rem' }}>{t}</span>
                            ))}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="text-center small">{r.periodsSpent}</td>
                      <td className="small text-muted" style={{ maxWidth: '150px' }}>{r.remarks || '-'}</td>
                      <td className="small text-muted text-nowrap">{formatTime(r.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherDailyReport;
